<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { cardActions } from "@/action/cardActions";
import { selectionActions } from "@/action/selectionActions";
import type { SelectionScope } from "@/action/selectionActions";
import { toggleActions } from "@/action/toggleActions";
import { action, actionMapWith } from "@/components/action/action";
import DeleteMenuItem from "@/components/menu/DeleteMenuItem/DeleteMenuItem.vue";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import { useBoardStore } from "@/store/board";
import { useSelectionStore } from "@/store/selection";

import OrganizeMenuItem from "./OrganizeMenuItem.vue";
import ShareMenuItem from "./ShareMenuItem.vue";

onMounted(() => {
  useSelectionStore().checkUndo();
});

const actions = actionMapWith<[SelectionScope]>("selection")(selectionActions);

const context = computed(() => useBoardStore().baseBoard.contextActions.selection || {});

const { t } = useI18n();

const undoText = ref("");

// use a delayed watch instead of a computed value
// because the tooltip popper here is contained inside the selectionActionMenu popper
// updating both at the same time can disturb the position calculation of the selection action popper
watch(
  () => useSelectionStore().lastHistoryEntry,
  (lastEntry) => {
    setTimeout(() => {
      undoText.value = lastEntry
        ? t("action.undoSelectionAction", {
            action: t(lastEntry.action.name),
            count: lastEntry.stateBefore.length,
          })
        : "";
    }, 100);
  },
  { immediate: true },
);

const trapOptions = computed(() => ({ extraElements: useBoardStore().selectedStickyIds }));

function canMirror() {
  return useSelectionStore().mirrorTargets.length > 0;
}
</script>

<template>
  <div v-focus-trap="trapOptions" style="display: contents">
    <IconButton v-bind="action(toggleActions.showSelectionOverview)" />
    <OrganizeMenuItem v-if="context.organize" />
    <IconButton v-if="context.mirror" v-bind="actions.openMirrorModal" :disabled="!canMirror()" />
    <IconButton v-if="context.team" v-bind="actions.openTeamModal" />
    <IconButton v-if="context.art" v-bind="actions.openArtModal" />
    <IconButton v-if="context.link" v-bind="action(cardActions.openLinkModal)" />
    <ShareMenuItem />
    <DeleteMenuItem :cards="useBoardStore().selectedStickies" />
    <IconButton v-bind="action(selectionActions.undo)" :text="undoText" :disabled="!undoText" />
  </div>
</template>
